import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import { Progress } from "@bowfast/ui/components/progress";
import { RadioGroup, RadioGroupItem } from "@bowfast/ui/components/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bowfast/ui/components/select";
import { Separator } from "@bowfast/ui/components/separator";
import { Switch } from "@bowfast/ui/components/switch";
import { Textarea } from "@bowfast/ui/components/textarea";
import { cn } from "@bowfast/ui/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  AnchorIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckCircle2Icon,
  CompassIcon,
  CreditCardIcon,
  EyeIcon,
  FlagIcon,
  GlobeIcon,
  LockIcon,
  MapIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  RotateCcwIcon,
  RulerIcon,
  SailboatIcon,
  SaveIcon,
  TagIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import * as z from "zod";
import { ExpeditionKind, VesselStatus } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { KIND_LABEL } from "~/lib/expedition-constants";
import { createSdk } from "~/lib/gql";
import { gradientForId } from "~/lib/gradients";
import { useOrg } from "~/lib/org-context";

// ── Schema ────────────────────────────────────────────────────────────────────

// Only the fields that are sent to the backend are validated here.
// Visibility, budget, berthFee, paidBerths, tags, description are UI-only —
// TODO(backend/expedition): add them once the schema supports them.
const expeditionSchema = z.object({
  name: z.string().min(3, "Give your voyage a name (at least 3 characters)"),
  maxCrew: z.number().int().min(1, "At least 1 berth required").max(50, "Max 50 berths"),
  kind: z.enum(ExpeditionKind).optional(),
  routeId: z.string().optional(),
  departureDate: z.iso
    .datetime({ offset: true })
    .optional()
    .or(
      z
        .string()
        .length(0)
        .transform(() => undefined),
    ),
  returnDate: z.iso
    .datetime({ offset: true })
    .optional()
    .or(
      z
        .string()
        .length(0)
        .transform(() => undefined),
    ),
});

type ExpeditionFormErrors = Partial<Record<keyof z.infer<typeof expeditionSchema>, string>>;

// ── Types ─────────────────────────────────────────────────────────────────────

// NOTE: Visibility, budget, berthFee, paidBerths, tags, description are UI-only.
// TODO(backend/expedition): add these fields to CreateExpeditionInput once the schema supports them.
type Visibility = "PRIVATE" | "ORGANISATION" | "PUBLIC";

interface Draft {
  title: string;
  kind: ExpeditionKind;
  description: string;
  routeId: string;
  departureDate: string;
  arrivalDate: string;
  departurePort: string;
  arrivalPort: string;
  distanceNm: string;
  distanceOverride: boolean;
  currency: string;
  budget: string;
  berthFee: string;
  paidBerths: boolean;
  tags: string[];
  visibility: Visibility;
  vesselIds: string[];
  berthsPerVessel: Record<string, number>;
}

// ── Static data ───────────────────────────────────────────────────────────────

// KIND_LABEL imported from ~/lib/expedition-constants

const KIND_ICONS: Record<ExpeditionKind, LucideIcon> = {
  OFFSHORE_PASSAGE: CompassIcon,
  COASTAL_CRUISE: SailboatIcon,
  SAIL_TRAINING: UsersIcon,
  RACE: FlagIcon,
  EXPEDITION: MapPinIcon,
  DELIVERY: ArrowRightIcon,
  RALLY: FlagIcon,
};

const INITIAL: Draft = {
  title: "",
  kind: ExpeditionKind.OffshorePassage,
  description: "",
  routeId: "",
  departureDate: "",
  arrivalDate: "",
  departurePort: "",
  arrivalPort: "",
  distanceNm: "",
  distanceOverride: false,
  currency: "EUR",
  budget: "",
  berthFee: "",
  paidBerths: false,
  tags: [],
  visibility: "ORGANISATION",
  vesselIds: [],
  berthsPerVessel: {},
};

// ── Completion helper ─────────────────────────────────────────────────────────

function useCompletion(draft: Draft) {
  return useMemo(() => {
    const checks = [
      { label: "Name the voyage", done: draft.title.trim().length > 0 },
      { label: "Choose type", done: true }, // always set
      { label: "Departure port", done: draft.departurePort.trim().length > 0 },
      { label: "Arrival port", done: draft.arrivalPort.trim().length > 0 },
      { label: "Set dates", done: draft.departureDate.length > 0 && draft.arrivalDate.length > 0 },
      { label: "Select vessel", done: draft.vesselIds.length > 0 },
      { label: "Add description", done: draft.description.trim().length > 0 },
    ];
    const done = checks.filter((c) => c.done).length;
    return { checks, done, total: checks.length, pct: Math.round((done / checks.length) * 100) };
  }, [
    draft.title,
    draft.departurePort,
    draft.arrivalPort,
    draft.departureDate,
    draft.arrivalDate,
    draft.vesselIds.length,
    draft.description,
  ]);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionCard({
  icon: Icon,
  title,
  children,
  action,
  delay = 0,
}: Readonly<{
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  delay?: number;
}>) {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay }}>
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-lg bg-abyss-50 dark:bg-abyss-800">
              <Icon className="size-4 text-abyss-500" />
            </div>
            <CardTitle className="text-[0.9375rem] font-heading font-semibold">{title}</CardTitle>
          </div>
          {action}
        </CardHeader>
        <CardContent className="pt-0">{children}</CardContent>
      </Card>
    </motion.div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function NewExpeditionPage() {
  const { user } = useAuth();
  const token = user?.access_token ?? "";
  const { orgId } = useOrg();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<Draft>(INITIAL);
  const [tagInput, setTagInput] = useState("");
  const [formErrors, setFormErrors] = useState<ExpeditionFormErrors>({});
  const completion = useCompletion(draft);

  // ── Data queries ────────────────────────────────────────────────────────────
  const { data: routesData } = useQuery({
    queryKey: ["routes", orgId],
    queryFn: () => createSdk(token).RoutesList({ orgId }),
    enabled: !!orgId && !!token,
  });
  const routes = routesData?.routes ?? [];

  const { data: vesselsData } = useQuery({
    queryKey: ["vessels", orgId],
    queryFn: () => createSdk(token).VesselsList({ orgId }),
    enabled: !!orgId && !!token,
  });
  const vessels = vesselsData?.vessels ?? [];

  // ── Create expedition mutation ───────────────────────────────────────────────
  const createExpedition = useMutation({
    mutationFn: (validated: z.infer<typeof expeditionSchema>) =>
      createSdk(token).CreateExpedition({
        orgId,
        input: {
          name: validated.name,
          maxCrew: validated.maxCrew,
          ...(validated.kind ? { kind: validated.kind } : {}),
          ...(validated.routeId ? { routeId: validated.routeId } : {}),
          ...(validated.departureDate ? { departureDate: validated.departureDate } : {}),
          ...(validated.returnDate ? { returnDate: validated.returnDate } : {}),
        },
      }),
    onSuccess: (result) => {
      navigate({
        to: "/expeditions/$expeditionSlug",
        params: { expeditionSlug: result.createExpedition.slug },
      });
    },
  });

  function handleSubmit() {
    const result = expeditionSchema.safeParse({
      name: draft.title,
      maxCrew: totalBerths > 0 ? totalBerths : 1,
      kind: draft.kind || undefined,
      routeId: draft.routeId || undefined,
      departureDate: draft.departureDate || undefined,
      returnDate: draft.arrivalDate || undefined,
    });
    if (!result.success) {
      const errors: ExpeditionFormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof ExpeditionFormErrors;
        if (key && !errors[key]) errors[key] = issue.message;
      }
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    createExpedition.mutate(result.data);
  }

  // ── Derived values ──────────────────────────────────────────────────────────
  const selectedRoute = routes.find((r) => r.id === draft.routeId);
  const selectedVessels = vessels.filter((v) => draft.vesselIds.includes(v.id));

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function addTag() {
    const t = tagInput.trim();
    if (t && !draft.tags.includes(t)) patch({ tags: [...draft.tags, t] });
    setTagInput("");
  }

  function selectRoute(routeId: string) {
    const route = routes.find((r) => r.id === routeId);
    if (!route) return;
    const sorted = [...route.waypoints].sort((a, b) => a.orderIndex - b.orderIndex);
    patch({
      routeId,
      departurePort: sorted[0]?.name ?? "",
      arrivalPort: sorted[sorted.length - 1]?.name ?? "",
      distanceNm: String(route.totalDistanceNm ?? ""),
      distanceOverride: false,
    });
  }

  function clearRoute() {
    patch({
      routeId: "",
      departurePort: "",
      arrivalPort: "",
      distanceNm: "",
      distanceOverride: false,
    });
  }

  function setVesselBerths(vesselId: string, count: number) {
    patch({ berthsPerVessel: { ...draft.berthsPerVessel, [vesselId]: Math.max(0, count) } });
  }

  const totalBerths = Object.values(draft.berthsPerVessel).reduce((sum, n) => sum + n, 0);
  const currencySymbol = draft.currency === "EUR" ? "€" : draft.currency === "GBP" ? "£" : "$";

  function toggleVessel(id: string) {
    if (draft.vesselIds.includes(id)) {
      const { [id]: _, ...rest } = draft.berthsPerVessel;
      patch({ vesselIds: draft.vesselIds.filter((vid) => vid !== id), berthsPerVessel: rest });
    } else {
      patch({
        vesselIds: [...draft.vesselIds, id],
        berthsPerVessel: { ...draft.berthsPerVessel, [id]: 6 },
      });
    }
  }

  return (
    <motion.div
      {...stagger}
      initial="initial"
      animate="animate"
      className="mx-auto w-full max-w-[1400px] p-4 md:p-6 lg:p-8"
    >
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 gap-1.5 text-muted-foreground"
            nativeButton={false}
            render={<Link to="/expeditions" />}
          >
            <ArrowLeftIcon className="size-3.5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-abyss-500 to-abyss-300 shadow-md">
              <CompassIcon className="size-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-[1.375rem] font-bold tracking-[-0.01em]">Plan a new voyage</h1>
              <p className="text-xs text-muted-foreground">Chart your course, assemble a crew, and set sail</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="draft" dotted>
            Draft
          </Badge>
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
            <SaveIcon className="size-3.5" />
            Save Draft
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            disabled={completion.pct < 60 || createExpedition.isPending}
            onClick={handleSubmit}
          >
            Set as Planning
            <CheckCircle2Icon className="size-3.5" />
          </Button>
        </div>
      </motion.div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        {/* ── Left: Form cards ── */}
        <div className="flex flex-col gap-5">
          {/* ── 1. Voyage identity ── */}
          <SectionCard icon={CompassIcon} title="Voyage identity" delay={0.02}>
            <div className="space-y-5">
              {/* Title */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Voyage name</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => {
                    patch({ title: e.target.value });
                    if (formErrors.name) {
                      const { name: _, ...rest } = formErrors;
                      setFormErrors(rest);
                    }
                  }}
                  placeholder="ARC 2026 — Atlantic crossing"
                  className={cn(
                    "font-heading text-base font-semibold",
                    formErrors.name && "border-destructive focus-visible:ring-destructive",
                  )}
                  aria-invalid={!!formErrors.name}
                  aria-describedby={formErrors.name ? "name-error" : undefined}
                />
                {formErrors.name && (
                  <p id="name-error" className="text-xs text-destructive">
                    {formErrors.name}
                  </p>
                )}
              </div>

              {/* Type selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Type of passage</Label>
                <RadioGroup
                  value={draft.kind as string}
                  onValueChange={(v) => {
                    if (v != null) patch({ kind: v as ExpeditionKind });
                  }}
                  className="grid grid-cols-2 gap-2 sm:grid-cols-4"
                >
                  {Object.values(ExpeditionKind).map((k) => {
                    const active = draft.kind === k;
                    const KIcon = KIND_ICONS[k];
                    return (
                      // biome-ignore lint/a11y/noLabelWithoutControl: label wraps RadioGroupItem
                      <label
                        key={k}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all",
                          active
                            ? "border-abyss-500 bg-abyss-50 text-abyss-700 shadow-sm dark:bg-abyss-900 dark:text-abyss-200"
                            : "border-border text-muted-foreground hover:border-abyss-200 hover:bg-muted/30",
                        )}
                      >
                        <RadioGroupItem value={k} className="sr-only" />
                        <KIcon
                          className={cn("size-3.5 shrink-0", active ? "text-abyss-500" : "text-muted-foreground/60")}
                        />
                        {KIND_LABEL[k]}
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  rows={3}
                  placeholder="Describe the voyage goals, highlights, and any crew requirements…"
                  value={draft.description}
                  onChange={(e) => patch({ description: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Shown to crew on the expedition listing.</p>
              </div>
            </div>
          </SectionCard>

          {/* ── 2. Route ── */}
          <SectionCard icon={MapPinIcon} title="Route" delay={0.06}>
            <div className="space-y-4">
              {/* Route selector */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Use a planned route</Label>
                {selectedRoute ? (
                  <div className="flex items-center gap-3 rounded-lg border border-abyss-500/30 bg-abyss-50/50 p-3 dark:bg-abyss-900/30">
                    <div className="flex size-8 items-center justify-center rounded-lg bg-abyss-100 dark:bg-abyss-800">
                      <MapIcon className="size-4 text-abyss-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{selectedRoute.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedRoute.waypoints.length} waypoints ·{" "}
                        {(selectedRoute.totalDistanceNm ?? 0).toLocaleString()} nm
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearRoute}
                      aria-label="Remove route"
                      className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <XIcon className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <Select
                    value={draft.routeId}
                    onValueChange={(v) => {
                      if (v != null) selectRoute(v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a route or enter ports manually…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {routes.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            <div className="flex items-center gap-2">
                              <MapIcon className="size-3 text-muted-foreground" />
                              <span>{r.name}</span>
                              <span className="ml-auto font-mono text-[0.625rem] text-muted-foreground">
                                {(r.totalDistanceNm ?? 0).toLocaleString()} nm
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  Pick an existing route or fill in ports below. Ports and distance auto-fill from the route.
                </p>
              </div>

              <Separator />

              {/* Departure → Arrival */}
              <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[1fr_auto_1fr]">
                {/* Departure */}
                <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                  <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    <AnchorIcon className="size-3" />
                    Departs from
                  </p>
                  <div className="space-y-2.5">
                    <Input
                      value={draft.departurePort}
                      onChange={(e) => patch({ departurePort: e.target.value })}
                      placeholder="Las Palmas de Gran Canaria"
                      aria-label="Departure port"
                    />
                    <div className="relative">
                      <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="date"
                        value={draft.departureDate}
                        onChange={(e) => patch({ departureDate: e.target.value })}
                        aria-label="Departure date"
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="hidden flex-col items-center gap-1 pt-10 text-muted-foreground/40 sm:flex">
                  <div className="flex size-8 items-center justify-center rounded-full border border-border bg-card">
                    <ArrowRightIcon className="size-4" />
                  </div>
                  <AnimatePresence>
                    {draft.distanceNm && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="mt-1 font-mono text-[0.625rem] font-semibold tabular-nums text-muted-foreground"
                      >
                        ~{Number(draft.distanceNm).toLocaleString()} nm
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                {/* Arrival */}
                <div className="rounded-xl border border-border bg-muted/20 p-4 dark:bg-abyss-900/30">
                  <p className="mb-3 flex items-center gap-1.5 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    <FlagIcon className="size-3" />
                    Arrives at
                  </p>
                  <div className="space-y-2.5">
                    <Input
                      value={draft.arrivalPort}
                      onChange={(e) => patch({ arrivalPort: e.target.value })}
                      placeholder="Rodney Bay, St Lucia"
                      aria-label="Arrival port"
                    />
                    <div className="relative">
                      <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="date"
                        value={draft.arrivalDate}
                        onChange={(e) => patch({ arrivalDate: e.target.value })}
                        aria-label="Estimated arrival"
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Distance & Budget row */}
              <Separator />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Distance</Label>
                    {selectedRoute && !draft.distanceOverride && (
                      <Badge variant="planning" className="text-[0.5625rem]">
                        from route
                      </Badge>
                    )}
                    {selectedRoute && draft.distanceOverride && (
                      <button
                        type="button"
                        onClick={() =>
                          patch({ distanceNm: String(selectedRoute.totalDistanceNm ?? ""), distanceOverride: false })
                        }
                        className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-abyss-500 hover:text-abyss-600"
                      >
                        <RotateCcwIcon className="size-2.5" />
                        Reset to route
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <RulerIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="pl-9 pr-10"
                      value={draft.distanceNm}
                      onChange={(e) => patch({ distanceNm: e.target.value, distanceOverride: selectedRoute != null })}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
                      nm
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Budget</Label>
                  <div className="relative">
                    <CreditCardIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <span className="pointer-events-none absolute left-9 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
                      {currencySymbol}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="pl-13"
                      value={draft.budget}
                      onChange={(e) => patch({ budget: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Currency</Label>
                  <Select
                    value={draft.currency}
                    onValueChange={(v) => {
                      if (v != null) patch({ currency: v });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="EUR">EUR — Euro</SelectItem>
                        <SelectItem value="GBP">GBP — Pound</SelectItem>
                        <SelectItem value="USD">USD — Dollar</SelectItem>
                        <SelectItem value="CHF">CHF — Franc</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Berth fee toggle */}
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Paid berths</Label>
                    <p className="text-xs text-muted-foreground">Charge crew a fee to join this expedition</p>
                  </div>
                  <Switch
                    checked={draft.paidBerths}
                    onCheckedChange={(checked) =>
                      patch({ paidBerths: checked, berthFee: checked ? draft.berthFee : "" })
                    }
                  />
                </div>
                <AnimatePresence>
                  {draft.paidBerths && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 gap-4 pb-1 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium">Fee per berth</Label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
                              {currencySymbol}
                            </span>
                            <Input
                              type="number"
                              min={0}
                              placeholder="0"
                              className="pl-7"
                              value={draft.berthFee}
                              onChange={(e) => patch({ berthFee: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="flex items-end">
                          <p className="pb-2.5 text-xs text-muted-foreground">
                            Crew must pay before their berth is confirmed. Funds go to your org&#39;s Stripe account.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </SectionCard>

          {/* ── 3. Vessels & berths ── */}
          <SectionCard
            icon={SailboatIcon}
            title="Vessels"
            delay={0.1}
            action={
              draft.vesselIds.length > 0 ? (
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[0.625rem] font-bold tabular-nums text-muted-foreground">
                    {draft.vesselIds.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => patch({ vesselIds: [] })}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear all
                  </button>
                </div>
              ) : undefined
            }
          >
            <p className="mb-3 text-xs text-muted-foreground">
              Select one or more vessels for this expedition. Crew can be assigned per vessel later.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {vessels.map((v) => {
                const selected = draft.vesselIds.includes(v.id);
                const isMaint = v.status === VesselStatus.UnderMaintenance;
                const gradient = gradientForId(v.id);
                const vesselType = [v.make, v.model].filter(Boolean).join(" ") || "Sailing vessel";
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => toggleVessel(v.id)}
                    className={cn(
                      "group relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all",
                      selected
                        ? "border-abyss-500 bg-abyss-50/50 ring-1 ring-abyss-500/20 dark:bg-abyss-900/50"
                        : "border-border hover:border-abyss-300 hover:bg-muted/30",
                      isMaint && !selected && "opacity-60",
                    )}
                  >
                    {/* Gradient avatar — matches VesselsListPage pattern */}
                    <div
                      className={cn(
                        "flex size-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-sm",
                        gradient,
                      )}
                    >
                      <SailboatIcon className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-sm font-semibold leading-tight">
                          {v.flagState ?? ""} {v.name}
                        </p>
                        {selected && <CheckCircle2Icon className="size-4 shrink-0 text-abyss-500" />}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{vesselType}</p>
                      {isMaint && (
                        <Badge variant="pending" className="mt-2 text-[0.625rem]">
                          Under maintenance
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Berths per vessel — shown when at least one vessel is selected */}
            <AnimatePresence>
              {selectedVessels.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <Separator className="my-4" />
                  <p className="mb-3 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    <UsersIcon className="mb-0.5 mr-1 inline size-3" />
                    Berths per vessel
                  </p>
                  <p className="mb-3 text-xs text-muted-foreground">
                    Set the number of crew berths on each vessel. Assign roles later on the expedition page.
                  </p>
                  <div className="space-y-2">
                    {selectedVessels.map((v) => {
                      const count = draft.berthsPerVessel[v.id] ?? 0;
                      const gradient = gradientForId(v.id);
                      return (
                        <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                          <div
                            className={cn(
                              "flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-sm",
                              gradient,
                            )}
                          >
                            <SailboatIcon className="size-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">
                              {v.flagState ?? ""} {v.name}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setVesselBerths(v.id, count - 1)}
                              disabled={count <= 0}
                              aria-label={`Decrease berths on ${v.name}`}
                              className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted disabled:opacity-30"
                            >
                              <MinusIcon className="size-3" />
                            </button>
                            <Input
                              type="number"
                              min={0}
                              max={20}
                              value={count}
                              onChange={(e) => setVesselBerths(v.id, Number.parseInt(e.target.value, 10) || 0)}
                              aria-label={`Berths on ${v.name}`}
                              className="h-7 w-12 text-center font-mono text-sm tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <button
                              type="button"
                              onClick={() => setVesselBerths(v.id, count + 1)}
                              aria-label={`Increase berths on ${v.name}`}
                              className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted"
                            >
                              <PlusIcon className="size-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-end gap-2 pt-1 text-xs text-muted-foreground">
                      <UsersIcon className="size-3" />
                      <span className="font-mono tabular-nums">{totalBerths}</span> berth{totalBerths !== 1 ? "s" : ""}{" "}
                      total
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </SectionCard>

          {/* ── 5. Visibility & Tags (combined) ── */}
          <SectionCard icon={EyeIcon} title="Visibility & tags" delay={0.18}>
            <div className="space-y-5">
              {/* Visibility */}
              <RadioGroup
                value={draft.visibility as string}
                onValueChange={(v) => {
                  if (v != null) patch({ visibility: v as Visibility });
                }}
                className="grid grid-cols-1 gap-2 sm:grid-cols-3"
              >
                {(
                  [
                    { value: "PRIVATE", label: "Private", desc: "Admins & assigned crew only", icon: LockIcon },
                    {
                      value: "ORGANISATION",
                      label: "Organisation",
                      desc: "All org members can view & apply",
                      icon: UsersIcon,
                    },
                    { value: "PUBLIC", label: "Public", desc: "Visible on the expedition board", icon: GlobeIcon },
                  ] as const
                ).map((opt) => {
                  const active = draft.visibility === opt.value;
                  const OIcon = opt.icon;
                  return (
                    // biome-ignore lint/a11y/noLabelWithoutControl: label wraps RadioGroupItem
                    <label
                      key={opt.value}
                      className={cn(
                        "flex flex-1 cursor-pointer items-start gap-3 rounded-xl border p-4 transition-all",
                        active
                          ? "border-abyss-500 bg-abyss-50 ring-1 ring-abyss-500/20 dark:bg-abyss-900"
                          : "border-border hover:border-abyss-200 hover:bg-muted/20",
                      )}
                    >
                      <RadioGroupItem value={opt.value} className="mt-0.5" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <OIcon className={cn("size-3.5", active ? "text-abyss-500" : "text-muted-foreground/60")} />
                          <span className={cn("text-sm font-semibold", active && "text-abyss-600 dark:text-abyss-300")}>
                            {opt.label}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>

              <Separator />

              {/* Tags */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Tags</Label>
                {/* NOTE: no tag-input primitive exists — raw input inside styled container */}
                <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg border border-input bg-transparent px-2.5 py-1.5 transition-shadow focus-within:ring-2 focus-within:ring-ring/50">
                  {draft.tags.map((t) => (
                    <span
                      key={t}
                      className="flex items-center gap-1 rounded-full bg-abyss-50 px-2.5 py-0.5 text-[0.625rem] font-semibold text-abyss-600 dark:bg-abyss-800 dark:text-abyss-300"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => patch({ tags: draft.tags.filter((x) => x !== t) })}
                        aria-label={`Remove tag ${t}`}
                        className="text-abyss-400 hover:text-abyss-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder={draft.tags.length === 0 ? "atlantic, offshore, rally…" : ""}
                    aria-label="Add tag"
                    className="h-5 min-w-20 flex-1 border-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Press Enter or comma to add.</p>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* ── Right: Sticky sidebar ── */}
        <div className="hidden lg:block">
          <div className="sticky top-6 space-y-5">
            {/* Completion progress */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-[0.9375rem] font-heading font-semibold">Readiness</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {completion.done} of {completion.total} complete
                      </span>
                      <span className="font-heading font-bold text-abyss-500">{completion.pct}%</span>
                    </div>
                    <Progress value={completion.pct} className="h-2" />
                    <div className="space-y-1.5 pt-1">
                      {completion.checks.map((c) => (
                        <div key={c.label} className="flex items-center gap-2 text-xs">
                          <div
                            className={cn(
                              "flex size-4 items-center justify-center rounded-full",
                              c.done ? "bg-shallow-500 text-white" : "border border-border bg-muted",
                            )}
                          >
                            {c.done && <CheckCircle2Icon className="size-3" />}
                          </div>
                          <span className={cn(c.done ? "text-muted-foreground line-through" : "text-foreground")}>
                            {c.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Live preview */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}>
              <Card className="overflow-hidden pt-0">
                {/* Mini hero — flush to card top */}
                <div className="relative bg-abyss-900 px-5 py-4 text-white">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: "radial-gradient(ellipse at 80% 20%, rgba(4,124,199,0.18) 0%, transparent 50%)",
                    }}
                  />
                  <div className="relative z-10">
                    <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-white/35">Preview</p>
                    <p className="mt-1 font-heading text-base font-bold leading-tight">
                      {draft.title || "Untitled voyage"}
                    </p>
                    <p className="mt-0.5 text-xs text-white/50">{KIND_LABEL[draft.kind]}</p>
                  </div>
                </div>
                <CardContent className="space-y-3 pt-4">
                  {/* Route name */}
                  {selectedRoute && (
                    <div className="flex items-center gap-2 text-xs">
                      <MapIcon className="size-3 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{selectedRoute.name}</span>
                    </div>
                  )}

                  {/* Route summary */}
                  {(draft.departurePort || draft.arrivalPort) && (
                    <div className="flex items-center gap-2 text-xs">
                      <AnchorIcon className="size-3 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{draft.departurePort || "—"}</span>
                      <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground/40" />
                      <span className="truncate font-medium">{draft.arrivalPort || "—"}</span>
                    </div>
                  )}

                  {/* Vessels */}
                  {selectedVessels.length > 0 && (
                    <div className="space-y-1">
                      {selectedVessels.map((sv) => (
                        <div key={sv.id} className="flex items-center gap-2 text-xs">
                          <SailboatIcon className="size-3 shrink-0 text-muted-foreground" />
                          <span className="truncate">
                            {sv.flagState ?? ""} {sv.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Berth fee */}
                  {draft.paidBerths && draft.berthFee && (
                    <div className="flex items-center gap-2 text-xs">
                      <CreditCardIcon className="size-3 shrink-0 text-muted-foreground" />
                      <span className="font-medium">
                        {currencySymbol}
                        {Number(draft.berthFee).toLocaleString()}
                      </span>
                      <span className="text-muted-foreground">per berth</span>
                    </div>
                  )}

                  {/* Berths summary */}
                  {totalBerths > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <UsersIcon className="size-3 shrink-0 text-muted-foreground" />
                      <span className="font-mono tabular-nums">{totalBerths}</span> berth{totalBerths !== 1 ? "s" : ""}
                    </div>
                  )}

                  {/* Distance */}
                  {draft.distanceNm && (
                    <div className="flex items-center gap-2 text-xs">
                      <RulerIcon className="size-3 shrink-0 text-muted-foreground" />
                      <span className="font-mono tabular-nums">{Number(draft.distanceNm).toLocaleString()} nm</span>
                    </div>
                  )}

                  {/* Tags */}
                  {draft.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {draft.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-abyss-50 px-2 py-0.5 text-[0.625rem] font-semibold text-abyss-600 dark:bg-abyss-800 dark:text-abyss-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Tips card */}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}>
              <Card className="border-shallow-200/50 bg-shallow-50/50 dark:border-shallow-800/30 dark:bg-shallow-950/30">
                <CardContent className="py-4">
                  <p className="text-[0.625rem] font-bold uppercase tracking-[0.06em] text-shallow-600 dark:text-shallow-400">
                    <TagIcon className="mb-0.5 mr-1 inline size-3" />
                    Skipper tips
                  </p>
                  <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-shallow-700 dark:text-shallow-300">
                    <li>A clear voyage name helps crew find your expedition quickly.</li>
                    <li>Set the berth count to match available bunks on your vessel.</li>
                    <li>Public expeditions appear on the crew board — great for finding new hands.</li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── Mobile sticky footer ── */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 p-3 backdrop-blur-sm lg:hidden">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="size-1.5 rounded-full bg-shallow-400" />
            <span>
              {completion.pct}% ready · {totalBerths} berth{totalBerths !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
              <SaveIcon className="size-3.5" />
              Draft
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={completion.pct < 60 || createExpedition.isPending}
              onClick={handleSubmit}
            >
              Set as Planning
              <CheckCircle2Icon className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
